import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, Plus, Trash2, ShieldCheck, ShieldOff, Info, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function SuperMinistry() {
  const [items, setItems] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", title: "",
    organization: "Ministère de la Santé Publique · RDC",
    province: "",  // vide = accès national
  });

  const load = async () => {
    try {
      const [r, rp] = await Promise.all([
        axios.get(`${API}/ministry/users`),
        axios.get(`${API}/ministry/provinces`),
      ]);
      setItems(r.data);
      setProvinces(rp.data);
    } catch (err) { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/ministry/create`, form);
      toast.success("Compte ministère créé — accès lecture seule aux stats nationales");
      setOpen(false);
      setForm({ name: "", email: "", password: "", title: "", organization: "Ministère de la Santé Publique · RDC" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const toggleActive = async (uid) => {
    try {
      await axios.patch(`${API}/ministry/users/${uid}/toggle`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (uid) => {
    if (!window.confirm("Supprimer définitivement ce compte ministère ?")) return;
    try {
      await axios.delete(`${API}/ministry/users/${uid}`);
      toast.success("Compte supprimé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="super-ministry-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Landmark className="w-6 h-6 text-[#1E4631]"/>
            <h1 className="text-2xl font-[Outfit] font-bold">Comptes Ministère de la Santé</h1>
          </div>
          <p className="text-sm text-[#5C6B61] mt-1 max-w-2xl">
            Accès <strong>lecture seule</strong> aux statistiques nationales agrégées (hôpitaux, patients, décès, hospitalisations, langues, communes).
            Aucune donnée nominative n'est exposée — le secret médical est respecté.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E4631] hover:bg-[#163424]" data-testid="new-ministry-btn">
              <Plus className="w-4 h-4 mr-2"/> Nouveau compte ministère
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Créer un compte ministère</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="ministry-form">
              <div className="p-3 bg-[#1E4631]/5 border border-[#1E4631]/20 rounded-lg text-xs text-[#5C6B61] flex gap-2">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-[#1E4631]"/>
                <div>Ce compte pourra <strong>consulter les statistiques nationales</strong> mais ne pourra <strong>ni voir</strong> les patients individuels <strong>ni modifier</strong> quoi que ce soit.</div>
              </div>
              <div><Label>Nom du responsable *</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} placeholder="Ex: Dr. Jean MUKENDI" data-testid="min-name"/></div>
              <div><Label>Fonction / Titre</Label><Input value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} placeholder="Ex: Directeur DPS Kinshasa" data-testid="min-title"/></div>
              <div><Label>Organisation</Label><Input value={form.organization} onChange={(e)=>setForm({...form, organization: e.target.value})} data-testid="min-org"/></div>
              <div><Label>Email de connexion *</Label><Input type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} placeholder="direction@sante.gouv.cd" data-testid="min-email"/></div>
              <div><Label>Mot de passe temporaire *</Label><Input type="password" required minLength={8} value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} placeholder="8 caractères minimum" data-testid="min-pw"/></div>
              <div>
                <Label>Portée d'accès</Label>
                <Select value={form.province || "__national__"} onValueChange={(v)=>setForm({...form, province: v === "__national__" ? "" : v})}>
                  <SelectTrigger data-testid="min-province"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__national__">🇨🇩 National — voit tous les hôpitaux RDC</SelectItem>
                    {provinces.map(p => <SelectItem key={p} value={p}>DPS {p} — uniquement les hôpitaux de {p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#5C6B61] mt-1">Choisissez une province pour créer un compte DPS (Division Provinciale de la Santé) limité à cette zone géographique.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={loading} className="bg-[#1E4631] hover:bg-[#163424]" data-testid="min-save">
                  {loading ? "..." : "Créer le compte"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3" data-testid="ministry-list">
        {items.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-[#E5EBE7] rounded-xl text-[#5C6B61]">
            Aucun compte ministère créé.<br/>
            <span className="text-xs">Cliquez sur "Nouveau compte ministère" pour créer le premier accès pour la Division Provinciale ou le Ministère.</span>
          </div>
        ) : items.map((m) => (
          <div key={m.id} className="bg-white rounded-xl border border-[#E5EBE7] p-5 flex items-start justify-between gap-4" data-testid={`ministry-${m.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Landmark className="w-4 h-4 text-[#1E4631]"/>
                <span className="font-semibold">{m.name}</span>
                {m.province ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#D96C4A]/10 text-[#D96C4A] font-medium inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3"/> DPS {m.province}
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E4631]/10 text-[#1E4631] font-medium">🇨🇩 National</span>
                )}
                {m.active === false ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#C62828]/10 text-[#C62828] font-medium">Désactivé</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#2E7D32]/10 text-[#2E7D32] font-medium">Actif</span>
                )}
              </div>
              {m.title && <div className="text-sm text-[#5C6B61] mt-0.5">{m.title}</div>}
              <div className="text-xs text-[#5C6B61] mt-1">{m.organization}</div>
              <div className="text-xs text-[#1E4631] mt-1 font-mono">{m.email}</div>
              <div className="text-[10px] text-[#8A9990] mt-1">Créé le {new Date(m.created_at).toLocaleDateString("fr-FR")}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                onClick={()=>toggleActive(m.id)}
                className="p-2 rounded-lg text-[#1E4631] hover:bg-[#1E4631]/10 transition"
                title={m.active === false ? "Réactiver" : "Désactiver"}
                data-testid={`toggle-min-${m.id}`}
              >
                {m.active === false ? <ShieldOff className="w-4 h-4"/> : <ShieldCheck className="w-4 h-4"/>}
              </button>
              <button
                onClick={()=>remove(m.id)}
                className="p-2 rounded-lg text-[#C62828] hover:bg-red-50 transition"
                title="Supprimer"
                data-testid={`delete-min-${m.id}`}
              >
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
